import { getLeverage } from '@ui/utils/trade/getLeverage';
import BigNumber from 'bignumber.js';

/**
 * Buffer on the minimum remaining collateral so the on-chain max-leverage check
 * still passes despite price drift between fetch and execution. The contract
 * computes notional from its own (possibly cached) market price, so a small
 * upward tick in pair price — and, for non-stable collateral like LVMON, drift
 * in the collateral price too — would otherwise push leverage past the cap.
 * Kept in step with LIQUIDATION_MARKET_BUFFER (0.1%); leaving resulting leverage
 * at ~maxLeverage / 1.001.
 */
const MAX_LEVERAGE_PRICE_BUFFER = new BigNumber('1.001');

export type MaxRemovableLimitReason = 'leverage' | 'liquidation' | 'takeProfit';

export type IsSafeAfterMarginRemoval = (
  collateralAfterRemoval: BigNumber,
) => boolean;

export type GetMaxTakeProfitPForLeverage = (
  leverage: BigNumber,
) => number | undefined;

export type MaxRemovableCollateralResult = {
  maxRemovable: BigNumber;
  /** Why removal is capped; null when nothing can be removed. */
  limitReason: MaxRemovableLimitReason | null;
  /** Upper bound from pair max leverage only (before liquidation cap). */
  maxRemovableByLeverage: BigNumber;
  /** Upper bound from current market PnL staying within max TP. */
  maxRemovableByTakeProfit: BigNumber | null;
  /**
   * Display leverage if maxRemovable is withdrawn, in `displayNotionalUsd`
   * basis (entry notional) so it lines up with the leverage shown elsewhere in
   * the app. NOT the market-notional leverage the contract caps against.
   */
  leverageAfterMaxRemoval: BigNumber | null;
};

/**
 * Max lvToken removable without exceeding pair max leverage, and optionally
 * without crossing liquidation at current market price.
 *
 * Leverage cap must use **market notional** (qty × market price), matching the
 * on-chain `removeMargin` check in TradingPortalFacet which computes
 * `notionalUsd = marketPrice * qty` rather than entry notional. Tier selection
 * upstream should also be driven by market notional (see
 * `getLeverageMarginByNotional`). Collateral amounts are always in lvToken units.
 *
 * `displayNotionalUsd` (entry notional) is used only to derive the
 * `leverageAfterMaxRemoval` number shown in the UI, so it agrees with the
 * leverage displayed in the position list / leverage-change rows. It does NOT
 * affect how much is removable. Defaults to `notionalUsd` when omitted.
 *
 * When `collateralDecimals` is provided the result is floored to that precision
 * so the on-chain amount can never round *up* past the safe cap (`toBigValue`
 * rounds half-up) and the "Max" hint matches the value that gets submitted.
 */
export const getMaxRemovableCollateral = ({
  collateral,
  notionalUsd,
  collateralPrice,
  maxLeverage,
  collateralDecimals,
  isSafeAfterRemoval,
  displayNotionalUsd,
  entryNotionalUsd,
  entryPrice,
  isLong,
  marketPrice,
  getMaxTakeProfitPForLeverage,
}: {
  collateral: BigNumber;
  /** Position notional at current market price (qty × marketPrice). */
  notionalUsd: BigNumber;
  collateralPrice: BigNumber;
  maxLeverage: number;
  /** lvToken decimals; when set, removable amounts are floored to it. */
  collateralDecimals?: number;
  isSafeAfterRemoval?: IsSafeAfterMarginRemoval;
  /** Entry notional (qty × entryPrice) for the displayed leverage only. */
  displayNotionalUsd?: BigNumber;
  /** Entry notional (qty × entryPrice), used to mirror removeMargin max TP checks. */
  entryNotionalUsd?: BigNumber;
  /** Entry price, used to mirror removeMargin max TP checks. */
  entryPrice?: BigNumber;
  isLong?: boolean;
  /** Current market price, used to cap removal by current PnL vs max TP. */
  marketPrice?: BigNumber;
  /** Returns max take-profit ratio as a decimal, e.g. 5.5 for 550%. */
  getMaxTakeProfitPForLeverage?: GetMaxTakeProfitPForLeverage;
}): MaxRemovableCollateralResult => {
  const empty = (): MaxRemovableCollateralResult => ({
    maxRemovable: new BigNumber(0),
    limitReason: null,
    maxRemovableByLeverage: new BigNumber(0),
    maxRemovableByTakeProfit: null,
    leverageAfterMaxRemoval: null,
  });

  if (
    collateral.lte(0) ||
    notionalUsd.lte(0) ||
    collateralPrice.lte(0) ||
    maxLeverage <= 0 ||
    !maxLeverage
  ) {
    return empty();
  }

  const floorToDecimals = (value: BigNumber) =>
    collateralDecimals === undefined
      ? value
      : value.decimalPlaces(collateralDecimals, BigNumber.ROUND_DOWN);

  const minCollateralUsd = notionalUsd.div(maxLeverage);
  const minCollateralTokens = minCollateralUsd
    .div(collateralPrice)
    .multipliedBy(MAX_LEVERAGE_PRICE_BUFFER);

  const maxRemovableByLeverage = floorToDecimals(
    BigNumber.max(collateral.minus(minCollateralTokens), 0),
  );

  let maxRemovable = maxRemovableByLeverage;
  let limitReason: MaxRemovableLimitReason | null = maxRemovable.gt(0)
    ? 'leverage'
    : null;
  let maxRemovableByTakeProfit: BigNumber | null = null;

  if (
    entryNotionalUsd?.gt(0) &&
    entryPrice?.gt(0) &&
    marketPrice?.gt(0) &&
    getMaxTakeProfitPForLeverage &&
    isLong !== undefined
  ) {
    maxRemovableByTakeProfit = floorToDecimals(
      capMaxRemovableByTakeProfit({
        collateral,
        collateralPrice,
        entryNotionalUsd,
        entryPrice,
        isLong,
        marketPrice,
        maxRemovable,
        getMaxTakeProfitPForLeverage,
      }),
    );

    if (maxRemovableByTakeProfit.lt(maxRemovable)) {
      maxRemovable = maxRemovableByTakeProfit;
      limitReason = 'takeProfit';
    }
  }

  if (isSafeAfterRemoval && maxRemovable.gt(0)) {
    const cappedByLiquidation = floorToDecimals(
      capMaxRemovableByLiquidation({
        collateral,
        maxRemovable,
        isSafeAfterRemoval,
      }),
    );

    if (cappedByLiquidation.lt(maxRemovable)) {
      maxRemovable = cappedByLiquidation;
      limitReason = 'liquidation';
    }
  }

  const collateralAfterMaxRemoval = collateral.minus(maxRemovable);
  const leverageAfterMaxRemoval =
    maxRemovable.gt(0) && collateralAfterMaxRemoval.gt(0)
      ? getLeverage({
          sizeUsd: displayNotionalUsd ?? notionalUsd,
          collateralUsd: collateralAfterMaxRemoval.times(collateralPrice),
        })
      : null;

  return {
    maxRemovable,
    limitReason,
    maxRemovableByLeverage,
    maxRemovableByTakeProfit,
    leverageAfterMaxRemoval,
  };
};

const capMaxRemovableByTakeProfit = ({
  collateral,
  collateralPrice,
  entryNotionalUsd,
  entryPrice,
  isLong,
  marketPrice,
  maxRemovable,
  getMaxTakeProfitPForLeverage,
}: {
  collateral: BigNumber;
  collateralPrice: BigNumber;
  entryNotionalUsd: BigNumber;
  entryPrice: BigNumber;
  isLong: boolean;
  marketPrice: BigNumber;
  maxRemovable: BigNumber;
  getMaxTakeProfitPForLeverage: GetMaxTakeProfitPForLeverage;
}): BigNumber => {
  const positivePriceDelta = isLong
    ? marketPrice.minus(entryPrice)
    : entryPrice.minus(marketPrice);

  if (positivePriceDelta.lte(0)) {
    return maxRemovable;
  }

  const positivePnlUsd = entryNotionalUsd.times(
    positivePriceDelta.div(entryPrice),
  );

  const isSafeAfterRemoval = (collateralAfterRemoval: BigNumber) => {
    const collateralAfterRemovalUsd =
      collateralAfterRemoval.times(collateralPrice);
    if (collateralAfterRemovalUsd.lte(0)) return false;

    const leverage = getLeverage({
      sizeUsd: entryNotionalUsd,
      collateralUsd: collateralAfterRemovalUsd,
    });
    const maxTakeProfitP = getMaxTakeProfitPForLeverage(leverage);
    if (!maxTakeProfitP || maxTakeProfitP <= 0) return true;

    return positivePnlUsd
      .multipliedBy(MAX_LEVERAGE_PRICE_BUFFER)
      .lte(collateralAfterRemovalUsd.times(maxTakeProfitP));
  };

  return capMaxRemovableByBinarySearch({
    collateral,
    maxRemovable,
    isSafeAfterRemoval,
  });
};

const capMaxRemovableByLiquidation = ({
  collateral,
  maxRemovable,
  isSafeAfterRemoval,
}: {
  collateral: BigNumber;
  maxRemovable: BigNumber;
  isSafeAfterRemoval: IsSafeAfterMarginRemoval;
}): BigNumber => {
  return capMaxRemovableByBinarySearch({
    collateral,
    maxRemovable,
    isSafeAfterRemoval,
  });
};

const capMaxRemovableByBinarySearch = ({
  collateral,
  maxRemovable,
  isSafeAfterRemoval,
}: {
  collateral: BigNumber;
  maxRemovable: BigNumber;
  isSafeAfterRemoval: IsSafeAfterMarginRemoval;
}): BigNumber => {
  if (!isSafeAfterRemoval(collateral)) {
    return new BigNumber(0);
  }

  let low = new BigNumber(0);
  let high = maxRemovable;

  for (let i = 0; i < 48; i++) {
    if (high.minus(low).lte(maxRemovable.times('1e-8'))) {
      break;
    }
    const mid = low.plus(high).div(2);
    const collateralAfter = collateral.minus(mid);

    if (collateralAfter.gt(0) && isSafeAfterRemoval(collateralAfter)) {
      low = mid;
    } else {
      high = mid;
    }
  }

  return low;
};
